// Shareable links: the map view and layer choices live in the URL hash,
// for example #map=8/43.70/-77.90&layer=currents&forecast=6&depth=10, or a
// saved past sample: &forecast=history&time=2026-10-05T06:00Z.
import {
  GREAT_LAKES_DEPTH_LEVELS_FEET,
  GREAT_LAKES_MAX_DEPTH_METERS,
  GREAT_LAKES_MAX_FORECAST_HOUR,
} from "./great-lakes-conditions.js";

export const LINK_LAYERS = Object.freeze(["temperature", "bottom-temperature", "air-temperature", "target-temperature", "thermocline", "currents", "waves", "wind", "upwelling", "none"]);
export const LINK_FORECASTS = Object.freeze([
  ...Array.from({ length: GREAT_LAKES_MAX_FORECAST_HOUR + 1 }, (_, hour) => String(hour)),
  "history",
]);

const number = (value) => (value === null || value === "" ? Number.NaN : Number(value));

export function parseViewHash(hash) {
  const params = new URLSearchParams(String(hash || "").replace(/^#/, ""));
  const result = {};
  if (params.has("map")) {
    const [zoom, latitude, longitude] = params.get("map").split("/").map(number);
    if (
      !Number.isFinite(zoom)
      || !Number.isFinite(latitude)
      || !Number.isFinite(longitude)
      || Math.abs(latitude) > 90
      || Math.abs(longitude) > 180
    ) return result;
    result.zoom = Math.max(3, Math.min(18, Math.round(zoom)));
    result.center = [latitude, longitude];
  }
  const layer = params.get("layer");
  if (LINK_LAYERS.includes(layer)) result.layer = layer === "none" ? "" : layer;
  const forecast = params.get("forecast");
  if (LINK_FORECASTS.includes(forecast)) result.forecast = forecast;
  const time = Date.parse(params.get("time") || "");
  if (forecast === "history" && Number.isFinite(time)) result.time = new Date(time).toISOString().replace(/:\d{2}\.\d{3}Z$/, "Z");
  const depth = number(params.get("depth"));
  if (Number.isFinite(depth) && depth >= 0) result.depth = Math.min(GREAT_LAKES_MAX_DEPTH_METERS, depth);
  const targetC = number(params.get("targetC"));
  if (Number.isFinite(targetC) && targetC >= 0 && targetC <= 30) result.targetC = targetC;
  if (["shallowest", "deepest"].includes(params.get("selection"))) result.selection = params.get("selection");
  if (["region", "lakes"].includes(params.get("coverage"))) result.coverage = params.get("coverage");
  if (params.get("contours") === "1") result.contours = true;
  if (params.get("bathymetry") === "1") result.bathymetry = true;
  return result;
}

export function viewHash({ center, zoom, layer, forecast, depth, time, targetC, selection, coverage, contours, bathymetry }) {
  const parts = [`map=${Math.round(zoom)}/${Number(center[0]).toFixed(4)}/${Number(center[1]).toFixed(4)}`];
  parts.push(`layer=${layer || "none"}`);
  if (forecast && forecast !== "0") parts.push(`forecast=${forecast}`);
  if (forecast === "history" && time) parts.push(`time=${String(time).replace(/:00Z$/, "Z")}`);
  if ((layer === "temperature" || layer === "currents") && Number(depth) > 0) parts.push(`depth=${Math.round(Number(depth))}`);
  if (layer === "target-temperature") {
    if (Number.isFinite(targetC)) parts.push(`targetC=${Number(targetC.toFixed(4))}`);
    if (selection === "deepest") parts.push("selection=deepest");
  }
  if (layer === "air-temperature" && coverage === "lakes") parts.push("coverage=lakes");
  if (contours) parts.push("contours=1");
  if (bathymetry) parts.push("bathymetry=1");
  return `#${parts.join("&")}`;
}

export function sharedViewHash(hash) {
  const value = String(hash || "");
  if (!value.startsWith("#")) throw new TypeError("Shared map hash must start with #");
  const separator = value.includes("?") ? "&" : value.includes("=") ? "&" : "";
  return `${value}${separator}shared=1`;
}

// Shared links (including older arbitrary depths) use the nearest fixed stop.
export function sliderFromDepth(depth) {
  const meters = Math.max(0, Math.min(GREAT_LAKES_MAX_DEPTH_METERS, Number(depth) || 0));
  return GREAT_LAKES_DEPTH_LEVELS_FEET.reduce((best, feet, index, levels) =>
    Math.abs(feet * 0.3048 - meters) < Math.abs(levels[best] * 0.3048 - meters) ? index : best, 0);
}
